import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DynamicDashboard, DynamicDashboardRef } from '@/components/dashboard/DynamicDashboard'
import { AddWidgetDialog } from '@/components/dashboard/AddWidgetDialog'
import { Button } from '@/components/ui/button'
import { Settings, X, RotateCcw, Plus, Save } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'

// Get greeting based on time of day
function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good Morning'
  if (hour < 17) return 'Good Afternoon'
  return 'Good Evening'
}

// Get first name from full name
function getFirstName(fullName: string): string {
  return fullName.split(' ')[0] || fullName
}

export default function DashboardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [showAddWidget, setShowAddWidget] = useState(false)
  const dashboardRef = useRef<DynamicDashboardRef>(null)
  const [isEditing, setIsEditing] = useState(false)

  const handleStartEditing = () => {
    dashboardRef.current?.startEditing()
    setIsEditing(true)
  }

  const handleCancelEditing = () => {
    dashboardRef.current?.cancelEditing()
    setIsEditing(false)
  }

  const handleSave = () => {
    dashboardRef.current?.save()
    setIsEditing(false)
  }

  const handleReset = () => {
    dashboardRef.current?.reset()
    setIsEditing(false)
  }

  const greeting = getGreeting()
  const firstName = user?.full_name ? getFirstName(user.full_name) : ''

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header with Greeting */}
      <div className="flex items-center justify-between flex-shrink-0 mb-6">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">OVERVIEW</p>
          <h1 className="text-3xl font-bold tracking-tight">
            {greeting}, {firstName}
          </h1>
          <p className="text-muted-foreground">
            Your agency performance for the last 30 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancelEditing}>
                <X className="h-4 w-4 mr-1" />
                {t('common.cancel')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleReset}>
                <RotateCcw className="h-4 w-4 mr-1" />
                {t('dashboard.resetToDefault')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAddWidget(true)}>
                <Plus className="h-4 w-4 mr-1" />
                {t('dashboard.addWidget')}
              </Button>
              <Button size="sm" onClick={handleSave}>
                <Save className="h-4 w-4 mr-1" />
                {t('common.save')}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={handleStartEditing}>
              <Settings className="h-4 w-4 mr-1" />
              {t('dashboard.customize')}
            </Button>
          )}
        </div>
      </div>

      {/* Dynamic Dashboard with Widgets - fills remaining space */}
      <div className="flex-1 min-h-0">
        <DynamicDashboard
          ref={dashboardRef}
          onAddWidget={() => setShowAddWidget(true)}
          onEditingChange={setIsEditing}
        />
      </div>

      {/* Add Widget Dialog */}
      <AddWidgetDialog
        open={showAddWidget}
        onOpenChange={setShowAddWidget}
      />
    </div>
  )
}
